import { z } from 'zod';
import { CHECKLIST_CONDITIONS, CHECKLIST_KINDS, LEASE_STATUSES, NOTICE_KINDS } from '../enums.js';
import { nonNegativeMoneySchema, positiveMoneySchema } from '../money.js';
import { dateOnlySchema, optionalText, text, uuidSchema } from './common.js';
import { isDepositWithinCap, maxSecurityDeposit } from '../michigan.js';
export const leaseStatusSchema = z.enum(LEASE_STATUSES);
export const checklistKindSchema = z.enum(CHECKLIST_KINDS);
export const checklistConditionSchema = z.enum(CHECKLIST_CONDITIONS);
export const noticeKindSchema = z.enum(NOTICE_KINDS);
export const leaseListQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    unitId: uuidSchema.optional(),
    status: leaseStatusSchema.optional(),
    /** Leases this tenant is named on, current and past. */
    tenantId: uuidSchema.optional(),
});
/**
 * The core lease-terms fields, factored out so `leasePreviewSchema` (the
 * New Lease wizard's preview step) can extend the exact same shape and
 * cross-field rules rather than a parallel copy that could drift.
 */
const leaseCoreShape = {
    unitId: uuidSchema,
    status: leaseStatusSchema.default('DRAFT'),
    startDate: dateOnlySchema,
    /** Null for month-to-month. */
    endDate: dateOnlySchema.nullish(),
    monthlyRent: positiveMoneySchema,
    securityDeposit: nonNegativeMoneySchema,
    /** Day of month rent is due. The 29th–31st clamp to month end at generation. */
    rentDueDay: z.coerce.number().int().min(1).max(31).default(1),
    /** Names on the lease. At least one; all are jointly liable. */
    tenantIds: z.array(uuidSchema).min(1).max(10),
    notes: optionalText(4000),
};
/**
 * The three cross-field rules, as standalone predicates rather than baked
 * into one generic `.refine()` chain — a generic helper here fights zod's
 * type inference (an optional field's "possibly undefined" widens once the
 * shape is generic). Structural typing lets both `createLeaseSchema` and
 * `leasePreviewSchema` apply the same three checks below without the logic
 * itself being duplicated.
 */
function endAfterStart(v) {
    return !v.endDate || v.endDate > v.startDate;
}
function noDuplicateTenants(v) {
    return new Set(v.tenantIds).size === v.tenantIds.length;
}
/**
 * MCL 554.602: deposit may not exceed 1.5x monthly rent. Enforced here so the
 * staff form and the API reject it identically, and at lease creation rather
 * than at move-in because the signed lease is what has to be compliant.
 */
function depositWithinCap(v) {
    return isDepositWithinCap(v.securityDeposit, v.monthlyRent);
}
/**
 * `isPreExisting`/`deferFirstMonthRent`/`openingBalanceAmount` are creation-only
 * — onboarding a lease that already existed before this system was in use.
 * They're on `createLeaseSchema` specifically, not `leaseCoreShape`: the
 * preview step generates the system's own placeholder contract, which a
 * pre-existing lease skips entirely (the wizard uploads the real signed PDF
 * instead), so `leasePreviewSchema` has no use for them.
 */
export const createLeaseSchema = z
    .object({
    ...leaseCoreShape,
    /** Onboarding a lease that predates this system. Gates the two fields below. */
    isPreExisting: z.boolean().default(false),
    /**
     * Skip generating this calendar month's RENT charge — for when
     * `openingBalanceAmount` already accounts for it, so the tenant isn't
     * billed twice. Sets `Lease.firstRentChargePeriodStart` to next month.
     */
    deferFirstMonthRent: z.boolean().default(false),
    /**
     * A balance already owed as of onboarding. Recorded as an
     * `OPENING_BALANCE` charge, atomically with the lease itself. Entering
     * this requires `ledger:write` in addition to `leases:write` — checked
     * in the route, not here, since capability checks aren't zod's job.
     */
    openingBalanceAmount: positiveMoneySchema.nullish(),
})
    .refine(endAfterStart, { message: 'endDate must be after startDate.', path: ['endDate'] })
    .refine(noDuplicateTenants, {
    message: 'A tenant may only be listed once on a lease.',
    path: ['tenantIds'],
})
    .refine(depositWithinCap, {
    message: 'Security deposit exceeds the Michigan cap of 1.5x monthly rent (MCL 554.602).',
    path: ['securityDeposit'],
})
    .refine((v) => v.isPreExisting || !v.openingBalanceAmount, {
    message: 'An opening balance may only be set for a pre-existing lease.',
    path: ['openingBalanceAmount'],
})
    .refine((v) => v.isPreExisting || !v.deferFirstMonthRent, {
    message: "Deferring the first month's rent only applies to a pre-existing lease.",
    path: ['deferFirstMonthRent'],
});
/**
 * The New Lease wizard's preview step. Same core fields and rules as
 * `createLeaseSchema` — nothing here is created yet, so `dependents`/`pets`
 * are plain name/relationship-or-species text, not calls into the real
 * dependents/pets endpoints (those need a real tenantId, which doesn't
 * apply until the lease actually exists).
 */
export const leasePreviewSchema = z
    .object(leaseCoreShape)
    .extend({
    addendumIds: z.array(uuidSchema).max(20).default([]),
    dependents: z
        .array(z.object({ name: text(200), relationship: text(100) }))
        .max(20)
        .default([]),
    pets: z
        .array(z.object({ name: text(200), species: text(100), breed: optionalText(100) }))
        .max(20)
        .default([]),
})
    .refine(endAfterStart, { message: 'endDate must be after startDate.', path: ['endDate'] })
    .refine(noDuplicateTenants, {
    message: 'A tenant may only be listed once on a lease.',
    path: ['tenantIds'],
})
    .refine(depositWithinCap, {
    message: 'Security deposit exceeds the Michigan cap of 1.5x monthly rent (MCL 554.602).',
    path: ['securityDeposit'],
});
/** The New Lease wizard's addendums step, applied once the lease is real. */
export const selectLeaseAddendumsSchema = z.object({
    addendumIds: z.array(uuidSchema).min(1).max(20),
});
export const updateLeaseSchema = z.object({
    status: leaseStatusSchema.optional(),
    endDate: dateOnlySchema.nullish(),
    rentDueDay: z.coerce.number().int().min(1).max(31).optional(),
    notes: optionalText(4000),
});
/** Helper for form-side display of the cap before submit. */
export { maxSecurityDeposit };
/**
 * Recording move-out starts the 30-day MCL 554.609 clock. The alert ladder keys
 * off `moveOutDate`, not the date this row was written, so backdating a move-out
 * correctly produces an already-urgent alert instead of a fresh 30 days.
 */
export const recordMoveOutSchema = z.object({
    leaseId: uuidSchema,
    moveOutDate: dateOnlySchema,
    forwardingAddressLine1: optionalText(200),
    forwardingCity: optionalText(100),
    forwardingState: optionalText(2),
    forwardingPostalCode: optionalText(10),
    notes: optionalText(4000),
});
export const checklistItemSchema = z.object({
    /** "Kitchen — countertop", "Bedroom 2 — carpet". */
    area: text(120),
    item: text(120),
    condition: checklistConditionSchema,
    notes: optionalText(2000),
    /** Populated on move-out only; drives the itemized damages notice. */
    estimatedCost: nonNegativeMoneySchema.nullish(),
});
export const createChecklistSchema = z.object({
    leaseId: uuidSchema,
    kind: checklistKindSchema,
    inspectedOn: dateOnlySchema,
    items: z.array(checklistItemSchema).min(1).max(500),
    tenantPresent: z.boolean().default(false),
});
/** MOVE_IN only — when the tenant returned their signed copy (MCL 554.608). */
export const recordChecklistReturnSchema = z.object({
    tenantReturnedOn: dateOnlySchema,
});
/**
 * Starts the MCL 554.603 14-day clock for the notice naming the bank. The
 * notice itself is generated by the service from this date — see
 * `DEPOSIT_NOTICE_DAYS` in michigan.ts.
 */
export const recordDepositReceivedSchema = z.object({
    depositReceivedOn: dateOnlySchema,
});
/** A roommate joining a lease already in progress. */
export const addLeaseTenantSchema = z.object({
    tenantId: uuidSchema,
    movedInOn: dateOnlySchema.nullish(),
});
/** A roommate leaving early. Sets removedOn — never deletes the row. */
export const removeLeaseTenantSchema = z.object({
    removedOn: dateOnlySchema,
});
/**
 * Manual/ad-hoc notice creation — lease renewals, rent increases, or anything
 * without a statutory due-date formula. DEPOSIT_NOTICE and DAMAGES_NOTICE are
 * generated by the service (recordDepositReceived / recordMoveOut) and are not
 * created through this endpoint.
 */
export const createNoticeSchema = z
    .object({
    leaseId: uuidSchema,
    kind: noticeKindSchema,
    triggeredOn: dateOnlySchema,
    dueBy: dateOnlySchema,
    notes: optionalText(4000),
})
    .refine((v) => v.kind !== 'DEPOSIT_NOTICE' && v.kind !== 'DAMAGES_NOTICE', {
    message: 'Deposit and damages notices are generated automatically, not created manually.',
    path: ['kind'],
})
    .refine((v) => v.dueBy >= v.triggeredOn, {
    message: 'dueBy must be on or after triggeredOn.',
    path: ['dueBy'],
});
/**
 * Confirm a notice went out. `documentId` points at the actual notice document,
 * attached separately through the documents upload flow — this endpoint never
 * generates or stores notice text itself.
 */
export const markNoticeSentSchema = z.object({
    sentAt: dateOnlySchema.optional(),
    /** e.g. "first-class mail" — the statute contemplates mail specifically. */
    sentVia: text(64),
    documentId: uuidSchema.nullish(),
});
//# sourceMappingURL=lease.js.map